// src/pages/ResetPasswordPage.jsx
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { auth } from "../utils/firebase";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);

  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    async function verifyCode() {
      if (!oobCode) { setStatus("invalid"); setError("Invalid or missing reset link. Please request a new one."); return; }
      try {
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setStatus("ready");
      } catch (err) {
        setError(
          err.code === "auth/expired-action-code"
            ? "This reset link has expired. Please request a new one."
            : "Invalid reset link. Please request a new one."
        );
        setStatus("invalid");
      }
    }
    verifyCode();
  }, [oobCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }

    setStatus("saving");
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus("success");
    } catch (err) {
      setError(
        err.code === "auth/expired-action-code"
          ? "This reset link has expired. Please request a new one."
          : "Failed to reset password. Please try again."
      );
      setStatus("ready");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--gray-50)", display: "flex", flexDirection: "column" }}>

      {/* NAV */}
      <nav style={{ borderBottom: "1px solid var(--gray-200)", padding: "0 32px", height: 60, display: "flex", alignItems: "center", background: "#fff" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <div style={{ width: 30, height: 30, background: "var(--blue)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 14 }}>📄</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 16, color: "var(--gray-900)" }}>
            ResumeScore<span style={{ color: "var(--blue)" }}>AI</span>
          </span>
        </Link>
      </nav>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px" }}>
        <div style={{ width: "100%", maxWidth: 420 }}>

          {/* Verifying */}
          {status === "verifying" && (
            <div className="card" style={{ padding: "48px 32px", textAlign: "center" }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid #DEE2E6", borderTopColor: "#185FA5", animation: "spin 0.7s linear infinite", margin: "0 auto 16px" }} />
              <p style={{ fontSize: 15, color: "var(--gray-500)" }}>Verifying reset link...</p>
            </div>
          )}

          {/* Invalid */}
          {status === "invalid" && (
            <div className="card" style={{ padding: "40px 32px", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
              <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Invalid Reset Link</h1>
              <p style={{ fontSize: 14, color: "var(--gray-500)", marginBottom: 28, lineHeight: 1.7 }}>{error}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button className="btn btn-primary btn-full btn-lg" onClick={() => navigate("/forgot-password")}>
                  Request New Reset Link
                </button>
                <button className="btn btn-outline btn-full" onClick={() => navigate("/login")}>
                  Back to Login
                </button>
              </div>
            </div>
          )}

          {/* Success */}
          {status === "success" && (
            <div className="card" style={{ padding: "40px 32px", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
              <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Password Reset!</h1>
              <p style={{ fontSize: 14, color: "var(--gray-500)", marginBottom: 8, lineHeight: 1.7 }}>
                Your password has been updated successfully.
              </p>
              <div style={{ background: "var(--blue-light)", color: "var(--blue)", fontSize: 13, fontWeight: 600, padding: "10px 16px", borderRadius: 8, marginBottom: 24 }}>
                {email}
              </div>
              <button className="btn btn-primary btn-full btn-lg" onClick={() => navigate("/login")}>
                Log In with New Password
              </button>
            </div>
          )}

          {/* Form */}
          {(status === "ready" || status === "saving") && (
            <div className="card" style={{ padding: "36px 32px" }}>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.02em" }}>Reset your password</h1>
                <p style={{ fontSize: 14, color: "var(--gray-500)", lineHeight: 1.6 }}>Creating new password for:</p>
                <div style={{ background: "var(--blue-light)", color: "var(--blue)", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8, marginTop: 8, display: "inline-block" }}>
                  {email}
                </div>
              </div>

              {error && (
                <div style={{ background: "#FCEBEB", color: "#A32D2D", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 20, border: "1px solid #f0a0a0" }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>New password</label>
                  <input
                    className="form-input"
                    type={showPasswords ? "text" : "password"}
                    placeholder="At least 6 characters"
                    value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); if (error) setError(""); }}
                    required autoFocus
                    disabled={status === "saving"}
                  />
                  {newPassword && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ height: 4, background: "var(--gray-200)", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 2, width: newPassword.length < 6 ? "25%" : newPassword.length < 10 ? "60%" : "100%", background: newPassword.length < 6 ? "#E24B4A" : newPassword.length < 10 ? "#BA7517" : "#3B6D11" }} />
                      </div>
                      <div style={{ fontSize: 11, marginTop: 3, color: newPassword.length < 6 ? "#A32D2D" : newPassword.length < 10 ? "#854F0B" : "#3B6D11" }}>
                        {newPassword.length < 6 ? "Weak" : newPassword.length < 10 ? "Fair" : "Strong"}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Confirm new password</label>
                  <input
                    className="form-input"
                    type={showPasswords ? "text" : "password"}
                    placeholder="Repeat your new password"
                    value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); if (error) setError(""); }}
                    required
                    disabled={status === "saving"}
                  />
                  {confirmPassword && (
                    <div style={{ fontSize: 11, marginTop: 4, color: newPassword === confirmPassword ? "#3B6D11" : "#A32D2D" }}>
                      {newPassword === confirmPassword ? "✓ Passwords match" : "✗ Passwords do not match"}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
                  <input type="checkbox" id="show" checked={showPasswords} onChange={e => setShowPasswords(e.target.checked)} style={{ cursor: "pointer" }} />
                  <label htmlFor="show" style={{ fontSize: 13, color: "var(--gray-600)", cursor: "pointer" }}>Show passwords</label>
                </div>

                <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={status === "saving"}>
                  {status === "saving"
                    ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Resetting...</>
                    : "Reset Password"
                  }
                </button>
              </form>

              <p style={{ textAlign: "center", marginTop: 16, fontSize: 14, color: "var(--gray-500)" }}>
                Remember your password?{" "}
                <Link to="/login" style={{ color: "var(--blue)", fontWeight: 500 }}>Log in</Link>
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}